/**
 * @file NotificationStream.jsx
 * @feature Notifications
 * @description Bell icon + dropdown that renders live SSE notifications.
 * Drop this anywhere in the app after the user profile is loaded.
 *
 * Props:
 *   userId      {string}    – from UserContext / user profile
 *   onViewAll   {function}  – called when "View all" is clicked (e.g. navigate('/notifications'))
 *
 * Behaviour:
 *   - Always shows the LAST 3 notifications in the dropdown (sliding window).
 *   - When total notifications >= 3, a "View all N notifications" button appears
 *     at the bottom of the dropdown with an underline accent.
 *   - As new notifications arrive, the window slides: shows n-2, n-1, n.
 *
 * Example:
 *   import NotificationStream from './components/NotificationStream';
 *   <NotificationStream userId={userProfile?.userId} onViewAll={() => navigate('/notifications')} />
 *
 * Note: No changes needed here — the SSE bridge lives entirely in
 * useNotificationStream.js. This component is UI-only.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNotificationStream, SSE_STATUS } from './useNotificationStream';

/* ─── tiny icon helpers (inline SVG — no icon-lib dependency) ─────────── */
const BellIcon = ({ hasUnread }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    {hasUnread && (
      <circle cx="18" cy="5" r="4" fill="#ef4444" stroke="white" strokeWidth="1.5" />
    )}
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ─── status dot ──────────────────────────────────────────────────────── */
const STATUS_COLOR = {
  [SSE_STATUS.IDLE]:         '#94a3b8',
  [SSE_STATUS.CONNECTING]:   '#f59e0b',
  [SSE_STATUS.CONNECTED]:    '#22c55e',
  [SSE_STATUS.RECONNECTING]: '#f59e0b',
  [SSE_STATUS.ERROR]:        '#ef4444',
  [SSE_STATUS.CLOSED]:       '#94a3b8',
};

const STATUS_LABEL = {
  [SSE_STATUS.IDLE]:         'Idle',
  [SSE_STATUS.CONNECTING]:   'Connecting…',
  [SSE_STATUS.CONNECTED]:    'Live',
  [SSE_STATUS.RECONNECTING]: 'Reconnecting…',
  [SSE_STATUS.ERROR]:        'Connection error',
  [SSE_STATUS.CLOSED]:       'Disconnected',
};

/* ─── notification type → accent colour ──────────────────────────────── */
const TYPE_ACCENT = {
  info:           '#3b82f6',
  alert:          '#ef4444',
  project_update: '#8b5cf6',
  task_assigned:  '#f59e0b',
  comment_added:  '#06b6d4',
};

const accentFor = (type) => TYPE_ACCENT[type] ?? '#64748b';

/* ─── relative time ──────────────────────────────────────────────────── */
const relativeTime = (isoString) => {
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/* ═══════════════════════════════════════════════════════════════════════ */

const NotificationStream = ({ userId, onViewAll }) => {
  const { notifications, status, clearAll, clearOne } = useNotificationStream(userId);
  const [open, setOpen] = useState(false);
  const dropdownRef     = useRef(null);

  const totalCount  = notifications.length;
  const unreadCount = notifications.filter((n) => !n.read).length;

  /**
   * Sliding window — always the last 3 notifications.
   * 1 notif  → shows [1]
   * 2 notifs → shows [1, 2]
   * 3 notifs → shows [1, 2, 3]  + "View all" button appears
   * 4 notifs → shows [2, 3, 4]  + "View all" button
   * 5 notifs → shows [3, 4, 5]  + "View all" button
   */
  const visibleNotifications = notifications.slice(-3);

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleViewAll = useCallback(() => {
    setOpen(false);
    if (typeof onViewAll === 'function') onViewAll();
  }, [onViewAll]);

  const styles = {
    wrapper: {
      position: 'relative',
      display:  'inline-block',
    },
    bell: {
      position:       'relative',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      width:          '40px',
      height:         '40px',
      border:         'none',
      borderRadius:   '10px',
      background:     'transparent',
      cursor:         'pointer',
      color:          'inherit',
      transition:     'background 0.15s',
    },
    badge: {
      position:      'absolute',
      top:           '4px',
      right:         '4px',
      minWidth:      '16px',
      height:        '16px',
      padding:       '0 3px',
      borderRadius:  '8px',
      background:    '#ef4444',
      color:         '#fff',
      fontSize:      '10px',
      fontWeight:    '700',
      lineHeight:    '16px',
      textAlign:     'center',
      pointerEvents: 'none',
    },
    dropdown: {
      position:      'absolute',
      top:           'calc(100% + 8px)',
      right:         '0',
      width:         '360px',
      display:       'flex',
      flexDirection: 'column',
      borderRadius:  '14px',
      background:    '#ffffff',
      boxShadow:     '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
      border:        '1px solid #e2e8f0',
      overflow:      'hidden',
      zIndex:        9999,
    },
    header: {
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'space-between',
      padding:        '14px 16px 12px',
      borderBottom:   '1px solid #f1f5f9',
      flexShrink:     0,
    },
    headerTitle: {
      fontWeight:  '700',
      fontSize:    '15px',
      color:       '#0f172a',
      marginRight: '8px',
    },
    statusDot: {
      display:       'inline-block',
      width:         '7px',
      height:        '7px',
      borderRadius:  '50%',
      marginRight:   '4px',
      verticalAlign: 'middle',
    },
    statusLabel: {
      fontSize:      '11px',
      color:         '#64748b',
      verticalAlign: 'middle',
    },
    clearBtn: {
      fontSize:     '12px',
      color:        '#64748b',
      background:   'none',
      border:       'none',
      cursor:       'pointer',
      padding:      '2px 6px',
      borderRadius: '6px',
    },
    list: {
      overflowY: 'auto',
      flexGrow:  1,
    },
    empty: {
      padding:   '40px 20px',
      textAlign: 'center',
      color:     '#94a3b8',
      fontSize:  '13px',
    },
    item: {
      display:      'flex',
      alignItems:   'flex-start',
      gap:          '0',
      padding:      '12px 16px',
      borderBottom: '1px solid #f8fafc',
      transition:   'background 0.1s',
      position:     'relative',
    },
    itemAccent: {
      width:        '3px',
      minHeight:    '40px',
      borderRadius: '2px',
      marginRight:  '12px',
      flexShrink:   0,
      alignSelf:    'stretch',
    },
    itemBody: {
      flex:     1,
      minWidth: 0,
    },
    itemHeader: {
      display:        'flex',
      justifyContent: 'space-between',
      alignItems:     'baseline',
      gap:            '8px',
      marginBottom:   '3px',
    },
    itemTitle: {
      fontWeight:   '600',
      fontSize:     '13px',
      color:        '#1e293b',
      whiteSpace:   'nowrap',
      overflow:     'hidden',
      textOverflow: 'ellipsis',
    },
    itemTime: {
      fontSize:   '11px',
      color:      '#94a3b8',
      flexShrink: 0,
    },
    itemMessage: {
      fontSize:          '12px',
      color:             '#475569',
      margin:            '0 0 6px',
      lineHeight:        '1.45',
      display:           '-webkit-box',
      WebkitLineClamp:   2,
      WebkitBoxOrient:   'vertical',
      overflow:          'hidden',
    },
    itemType: {
      display:       'inline-block',
      fontSize:      '10px',
      fontWeight:    '600',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      padding:       '1px 6px',
      borderRadius:  '4px',
      border:        '1px solid',
    },
    dismissBtn: {
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      width:          '24px',
      height:         '24px',
      flexShrink:     0,
      background:     'none',
      border:         'none',
      borderRadius:   '6px',
      cursor:         'pointer',
      color:          '#94a3b8',
      marginLeft:     '8px',
      marginTop:      '1px',
      transition:     'background 0.1s, color 0.1s',
    },
    /* ── View all button — shown when totalCount >= 3 ── */
    viewAllBtn: {
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'space-between',
      width:           '100%',
      padding:         '11px 16px',
      fontSize:        '13px',
      fontWeight:      '600',
      color:           '#534AB7',
      background:      '#EEEDFE',
      border:          'none',
      borderTop:       '1px solid #CECBF6',
      /* underline accent on the bottom-right label */
      cursor:          'pointer',
      transition:      'background 0.15s',
      flexShrink:      0,
    },
    viewAllLabel: {
      display:        'flex',
      alignItems:     'center',
      gap:            '4px',
      fontSize:       '12px',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
  };

  return (
    <div style={styles.wrapper} ref={dropdownRef}>
      {/* ── Bell trigger ── */}
      <button
        style={styles.bell}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications — ${unreadCount} unread`}
        title="Notifications"
      >
        <BellIcon hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div style={styles.dropdown}>

          {/* Header */}
          <div style={styles.header}>
            <div>
              <span style={styles.headerTitle}>Notifications</span>
              <span style={{ ...styles.statusDot, background: STATUS_COLOR[status] }} />
              <span style={styles.statusLabel}>{STATUS_LABEL[status]}</span>
            </div>
            {notifications.length > 0 && (
              <button style={styles.clearBtn} onClick={clearAll}>
                Clear all
              </button>
            )}
          </div>

          {/* List — sliding window of last 3 */}
          <div style={styles.list}>
            {visibleNotifications.length === 0 ? (
              <div style={styles.empty}>
                {status === SSE_STATUS.CONNECTING || status === SSE_STATUS.RECONNECTING
                  ? '⏳ Connecting to live updates…'
                  : status === SSE_STATUS.ERROR
                  ? '⚠️ Could not connect. Retrying later.'
                  : '✓ No new notifications'}
              </div>
            ) : (
              visibleNotifications.map((n) => (
                <div key={n.id} style={styles.item}>
                  <div style={{ ...styles.itemAccent, background: accentFor(n.type) }} />
                  <div style={styles.itemBody}>
                    <div style={styles.itemHeader}>
                      <span style={styles.itemTitle}>{n.title}</span>
                      <span style={styles.itemTime}>{relativeTime(n.timestamp)}</span>
                    </div>
                    {n.message && (
                      <p style={styles.itemMessage}>{n.message}</p>
                    )}
                    <span style={{
                      ...styles.itemType,
                      color:       accentFor(n.type),
                      borderColor: accentFor(n.type) + '44',
                    }}>
                      {n.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <button
                    style={styles.dismissBtn}
                    onClick={() => clearOne(n.id)}
                    aria-label="Dismiss"
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* ── View all button — appears when total >= 3 ── */}
          {totalCount >= 3 && (
            <button
              style={styles.viewAllBtn}
              onClick={handleViewAll}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#CECBF6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#EEEDFE'; }}
            >
              <span>View all {totalCount} notifications</span>
              <span style={styles.viewAllLabel}>
                See all
                <ChevronRight size={14} />
              </span>
            </button>
          )}

        </div>
      )}
    </div>
  );
};

export default NotificationStream;